import {
  GetterTree, Module, MutationTree, ActionTree, ActionContext,
} from 'vuex'
import { RootState } from '..'
import { RestParty } from '@/rest/party'
import { RestTrack } from '@/rest/track'
import { Track } from '@/ts/interfaces/track'
import { RestPlayer } from '@/rest/player'
import { PlayerStatus } from '@/ts/interfaces/player-status'
import { Error } from '@/ts/interfaces/error'
import { Party } from '@/ts/interfaces/party'
import { DEFAULT_MUSIC_COUNTRY } from '@/constants/country'
import { Follow, FollowRaw } from '@/ts/interfaces/follow'
import { cms } from '@/cms'

const restParty = new RestParty()
const restTrack = new RestTrack()
const restPlayer = new RestPlayer()

export interface PartyState {
  id: number
  name: string
  createdAt: Date | null,
  updatedAt: Date | null,
  startedAt: Date | null,
  tracks: Track[]
  tracksExluded: Track[]
  status: null | PlayerStatus
  musicType: string
  musicId: string
  musicService: 'spotify' | 'apple',
  musicCountry: string
  isVote: boolean
  isAutoStart: boolean
  waitingRoomMinutes: number
  startAllowed: string
  wpPostId: string | null
  isPlayable: boolean
  baseUrl: string
  follow: Follow
  loginRequired: boolean
  startsAt: Date
  endsAt: Date
}

const partyState: PartyState = {
  id: 0,
  name: '',
  createdAt: null,
  updatedAt: null,
  startedAt: null,
  tracks: [],
  tracksExluded: [],
  status: null,
  musicType: '',
  musicId: '',
  musicService: 'spotify',
  musicCountry: DEFAULT_MUSIC_COUNTRY,
  isVote: true,
  isAutoStart: false,
  waitingRoomMinutes: 30,
  startAllowed: 'host',
  wpPostId: null,
  isPlayable: true,
  baseUrl: '/',
  follow: {
    apple: {
      album: '',
      playlist: '',
      artists: [''],
    },
    spotify: {
      album: '',
      playlist: '',
      artists: [''],
    },
  },
  loginRequired: true,
  startsAt: new Date(),
  endsAt: new Date(),
}

const mutations: MutationTree<PartyState> = {
  PARTY_SET_ID(state: PartyState, id: number) {
    state.id = id
  },
  PARTY_SET_NAME(state: PartyState, name: string) {
    state.name = name
  },
  PARTY_SET_CREATED_AT(state: PartyState, createdAt: Date) {
    state.createdAt = createdAt
  },
  PARTY_SET_UPDATED_AT(state: PartyState, updatedAt: Date) {
    state.updatedAt = updatedAt
  },
  PARTY_SET_STARTED_AT(state: PartyState, startedAt: Date) {
    state.startedAt = startedAt
  },
  PARTY_SET_TRACKS(state: PartyState, tracks: Track[]) {
    state.tracks = tracks
  },
  PARTY_SET_TRACKS_EXCLUDED(state: PartyState, tracksExluded: Track[]) {
    state.tracksExluded = tracksExluded
  },
  PARTY_SET_STATUS(state: PartyState, status) {
    state.status = status
  },
  PARTY_SET_MUSIC_TYPE(state: PartyState, musicType) {
    state.musicType = musicType
  },
  PARTY_SET_MUSIC_ID(state: PartyState, musicId) {
    state.musicId = musicId
  },
  PARTY_SET_MUSIC_SERVICE(state: PartyState, musicService) {
    state.musicService = musicService
  },
  PARTY_SET_MUSIC_COUNTRY(state: PartyState, musicCountry) {
    state.musicCountry = musicCountry
  },
  PARTY_SET_IS_VOTE(state: PartyState, isVote) {
    state.isVote = isVote
  },
  PARTY_SET_IS_AUTO_START(state: PartyState, isAutoStart) {
    state.isAutoStart = isAutoStart
  },
  PARTY_SET_WAITING_ROOM_MINUTES(state: PartyState, waitingRoomMinutes) {
    state.waitingRoomMinutes = waitingRoomMinutes
  },
  PARTY_SET_START_ALLOWED(state: PartyState, startAllowed) {
    state.startAllowed = startAllowed
  },
  PARTY_SET_WP_POST_ID(state: PartyState, wpPostId: string | null) {
    state.wpPostId = wpPostId
  },
  PARTY_SET_BASE_URL(state: PartyState, url: string) {
    state.baseUrl = url
  },
  PARTY_SET_IS_PLAYABLE(state: PartyState, isPlayable: boolean) {
    state.isPlayable = isPlayable
  },
  PARTY_SET_FOLLOW(state: PartyState, follow: Follow) {
    state.follow = follow
  },
  PARTY_SET_LOGIN_REQUIRED(state: PartyState, loginRequired: boolean) {
    state.loginRequired = loginRequired
  },
  PARTY_SET_STARTS_AT(state: PartyState, startsAt: Date) {
    state.startsAt = startsAt
  },
  PARTY_SET_ENDS_AT(state: PartyState, endsAt: Date) {
    state.endsAt = endsAt
  }
}

const actions: ActionTree<PartyState, RootState> = {
  async PARTY_EDIT(ctx: ActionContext<PartyState, RootState>) {
    let raw: Party
    try {
      raw = await restParty.edit(
        ctx.state.id,
        ctx.state.name,
        ctx.state.isVote,
        ctx.state.isAutoStart,
        ctx.state.wpPostId,
        ctx.getters.frontendData,
        ctx.getters.partyFollowMapped,
        ctx.getters.partyLoginRequired,
      )
    } catch (e) {
      console.error(e)
      return null
    }

    ctx.commit('PARTY_SET_CREATED_AT', raw.created_at !== null ? new Date(raw.created_at) : null)
    ctx.commit('PARTY_SET_UPDATED_AT', raw.updated_at !== null ? new Date(raw.updated_at) : null)
    ctx.commit('PARTY_SET_STARTED_AT', raw.started_at !== null ? new Date(raw.started_at) : null)
    ctx.commit('PARTY_SET_NAME', raw.name)
    ctx.commit('PARTY_SET_IS_VOTE', raw.voting_enabled)
    ctx.commit('PARTY_SET_IS_AUTO_START', raw.auto_start)
    ctx.commit('PARTY_SET_WP_POST_ID', raw.wp_post_id)
    ctx.dispatch('SET_DATA', raw.frontend)
  },
  async PARTY_GET_BY_ID(ctx: ActionContext<PartyState, RootState>): Promise<void> {
    const raw = await restParty.get(ctx.state.id)
    ctx.commit('PARTY_SET_CREATED_AT', raw.created_at !== null ? new Date(raw.created_at) : null)
    ctx.commit('PARTY_SET_UPDATED_AT', raw.updated_at !== null ? new Date(raw.updated_at) : null)
    ctx.commit('PARTY_SET_STARTED_AT', raw.started_at !== null ? new Date(raw.started_at) : null)
    ctx.commit('PARTY_SET_NAME', raw.name)
    ctx.commit('PARTY_SET_IS_VOTE', raw.voting_enabled)
    ctx.commit('PARTY_SET_IS_AUTO_START', raw.auto_start)
    ctx.commit('PARTY_SET_WP_POST_ID', raw.wp_post_id)
    ctx.commit('PARTY_SET_LOGIN_REQUIRED', raw.login_required)
    ctx.dispatch('SET_DATA', raw.frontend)
  },
  async PARTY_LOAD_TRACKS(ctx: ActionContext<PartyState, RootState>): Promise<void> {
    const { items, options, excluded } = await restTrack.get(ctx.state.id)

    if (!options) {
      return
    }

    ctx.commit('PARTY_SET_TRACKS_EXCLUDED', excluded)
    ctx.commit('PARTY_SET_TRACKS', items)
    ctx.commit('PARTY_SET_MUSIC_TYPE', options.type)
    ctx.commit('PARTY_SET_MUSIC_ID', options.id)
    ctx.commit('PARTY_SET_MUSIC_SERVICE', options.service)
    ctx.commit('PARTY_SET_MUSIC_COUNTRY', options.country || DEFAULT_MUSIC_COUNTRY)

    await ctx.dispatch('PLAYER_ENQUEUE_TRACKS', items)
  },
  async PARTY_UPDATE_TRACKS(ctx: ActionContext<PartyState, RootState>, params: { type: string, id: string, country: string }) {
    const { items, options, excluded } = await restTrack.update(
      ctx.state.id,
      params.type,
      params.id,
      ctx.state.musicService,
      params.country,
    )

    ctx.commit('PARTY_SET_TRACKS_EXCLUDED', excluded)
    ctx.commit('PARTY_SET_TRACKS', items)
    ctx.commit('PARTY_SET_MUSIC_TYPE', options.type)
    ctx.commit('PARTY_SET_MUSIC_ID', options.id)
    ctx.commit('PARTY_SET_MUSIC_SERVICE', options.service)
    ctx.commit('PARTY_SET_MUSIC_COUNTRY', options.country || DEFAULT_MUSIC_COUNTRY)
  },
  async PARTY_TRACK_VOTE(ctx: ActionContext<PartyState, RootState>, p: { trackId: string }): Promise<void> {
    await restTrack.vote(ctx.getters.userToken, ctx.state.id, p.trackId)
  },
  async PARTY_TRACK_UNVOTE(ctx: ActionContext<PartyState, RootState>, p: { trackId: string }): Promise<void> {
    await restTrack.unvote(ctx.getters.userToken, ctx.state.id, p.trackId)
  },
  async PARTY_START(ctx: ActionContext<PartyState, RootState>): Promise<void> {
    await restParty.start(ctx.getters.userToken, ctx.state.id)
  },
  async PARTY_STATUS(ctx: ActionContext<PartyState, RootState>): Promise<void> {
    await ctx.dispatch('USER_LOAD_SERVICE_TOKEN_IF_NEEDED')

    return restPlayer.status(ctx.state.id)
      .then((res) => {
        ctx.commit('PARTY_SET_STATUS', res)
      })
  },
  PARTY_STOP(ctx: ActionContext<PartyState, RootState>) {
    return restParty.stop(ctx.state.id)
  },
  async PARTY_END(ctx: ActionContext<PartyState, RootState>) {
    const data = await restParty.end(ctx.state.id)
    ctx.commit('PARTY_SET_ENDS_AT', new Date(data.ends_at))
  },
}

const getters: GetterTree<PartyState, any> = {
  partyId: (state: PartyState) => state.id,
  partyName: (state: PartyState) => state.name,
  partyEndsAt: (state: PartyState) => state.endsAt,
  partyStartsAt: (state: PartyState) => state.startsAt,
  partyStartedsAt: (state: PartyState) => state.startedAt,
  partyCreatedAt: (state: PartyState) => state.createdAt,
  partyUpdatedAt: (state: PartyState) => state.updatedAt,
  partyDateIsStarted: (state: PartyState, g: any) => g.partyStartsAt <= new Date(Date.now() + g.partyWaitingRoomMS),
  partyDateIsEnded: (state: PartyState, g: any) => () => g.partyEndsAt <= new Date(),
  partyServices: (state: PartyState) => cms.campaign.enabled_services,
  partyLoginServices: (state: PartyState) => cms.campaign.enabled_services.filter((service) => service !== 'umg_hls'), // TODO: Handle in CMS
  partyTracks: (state: PartyState) => state.tracks,
  partyTracksExluded: (state: PartyState) => state.tracksExluded,
  partyTracksActive: (state: PartyState) => state.tracks.map((track, index) => ({
    ...track,
    played: (state.tracks.findIndex((t) => t.id === state.status?.track.id) > index),
    current: state.status?.track.id === track.id,
  })),
  partyIsStarted: (state: PartyState) => state.status !== null,
  partyStatus: (state: PartyState) => state.status,
  partyMusicId: (state: PartyState) => state.musicId,
  partyMusicService: (state: PartyState) => state.musicService,
  partyMusicCountry: (state: PartyState) => state.musicCountry,
  partyMusicType: (state: PartyState) => state.musicType,
  partyIsVote: (state: PartyState) => state.isVote,
  partyIsAutoStart: (state: PartyState) => state.isAutoStart,
  partyIsWaitingRoomMinutes: (state: PartyState) => state.waitingRoomMinutes,
  partyWaitingRoomMS: (state: PartyState) => (state.waitingRoomMinutes * 60 * 1000),
  partyStartAllowed: (state: PartyState) => state.startAllowed,
  partyWpPostId: (state: PartyState) => state.wpPostId,
  partyIsStartAllowed: (state: PartyState, g: any) => {
    switch (state.startAllowed) {
      case 'host': {
        return g.userIsHost
      }
      case 'admin': {
        return g.userIsAdmin
      }
      case 'both': {
        return g.userIsHost || g.userIsAdmin
      }
      default: {
        return false
      }
    }
  },
  partyIsPlayable: (state: PartyState) => state.isPlayable,
  partyBaseUrl: (state: PartyState) => state.baseUrl,
  partyFollow: (state: PartyState) => state.follow,
  partyFollowMapped: (state: PartyState) => {
    const items: FollowRaw[] = []

    Object.keys(state.follow).forEach((service) => {
      Object.keys(state.follow[service]).forEach((type) => {
        const id = (state.follow[service] as any)[type]

        if (type === 'artists') {
          id.forEach((artistId: string) => {
            items.push({ type: 'artist', service, id: artistId })
          })
        } else {
          items.push({ type, service, id })
        }
      })
    })

    return items.filter((item) => item.id && item.id !== '')
  },
  partyLoginRequired: (state: PartyState): boolean => state.loginRequired,
}

export const partyModule: Module<PartyState, any> = {
  state: partyState,
  mutations,
  actions,
  getters,
}
